<script setup lang="ts">
import { marked } from 'marked'
import axios from 'axios'
import type { NovaChatbotProps } from './NovaChatbot.types'
import type { ChatBotUIContent, ChatBotUIContents } from '@store'
import { ROUTES } from '@configs'
import { NovaModalDialog } from '#components'

const props = defineProps<NovaChatbotProps>()
const { show: modalShow, hide: modalHide } = useModal()
const { dayjs } = useDayjs()
const { t } = useI18n()
const userStore = useUserStore()
const { user } = storeToRefs(userStore)
const { gtEvent } = useGoogleTag()
const chatBotStore = useChatBotStore()

const isLoading = ref(false)
const isOver = ref(false)
const message = ref('')
const chatInput = ref<Nullable<HTMLTextAreaElement>>(null)
const chatInputRerenderKey = ref(0)
const chatbotServiceController = ref<Nullable<AbortController>>(null)
const chatbotContents = ref<ChatBotUIContents>([])

/**
 * UTIL: role 에 기반한 사용자 프로필 이미지 생성
 */
const genProfileImage = (role: ChatBotUIContent['role']) => {
  if (role === 'CHATBOT') {
    return '/icon-chatbot-avatar.png'
  }
  return user.value?.userProflUrl
    ? user.value?.userProflUrl
    : '/icon-chatbot-avatar-default.svg?url'
}

/**
 * UTIL: markdown -> html
 */
const convertedMarkdownToHtml = (markdown: string) => {
  const replaced = markdown.replaceAll('\\n', '\n')
  // console.log(replaced)
  return marked(replaced, {
    gfm: true,
    breaks: true,
  })
}

/**
 * UTIL: 챗봇 메시지 입력창 높이 조절
 */
const autoResize = () => {
  const el = chatInput.value
  if (el) {
    el.style.height = 'auto' // 높이를 초기화한 후 scrollHeight로 갱신
    const maxHeight = 20 * 3 // 줄높이 20px * 3줄
    const newHeight = Math.min(el.scrollHeight, maxHeight)
    el.style.height = `${newHeight}px`
    el.style.overflowY = el.scrollHeight > maxHeight ? 'auto' : 'hidden'
  }
}

/**
 * CALLBACK HANDLER: 챗봇 메시지 박스 키(down)
 */
const handleOnKeydown = (evt: KeyboardEvent) => {
  if (evt.key === 'Enter' && !evt.shiftKey) {
    evt.preventDefault()
  }
}

/**
 * CALLBACK HANDLER: 챗봇 신규 매시지 입력
 */
const handleNewMessage = (evt?: KeyboardEvent) => {
  // 1. 챗본 문의
  if (evt?.key === 'Enter' && !evt?.shiftKey) {
    evt.preventDefault()
  }

  if (!message.value) return
  if (isLoading.value) return

  console.log('CHATBOT_START:', message.value)

  // 1. 유저 버블 추가
  chatbotContents.value.push({
    content: [...message.value].join('').replaceAll('\n', '<br />'),
    role: 'USER',
    timestamp: dayjs().format('A hh:mm'),
  })

  // 2. 챗봇 문의
  const prevChatLogSn = chatbotContents.value.findLast(
    (item) => item.role === 'CHATBOT'
  )?.chatLogSn
  fetchChatBot(message.value, prevChatLogSn)

  // 3. 클리어
  message.value = ''
  chatInputRerenderKey.value = ++chatInputRerenderKey.value
  nextTick(() => {
    chatInput.value?.focus()
  })
}

/**
 * FETCHER: 챗봇 채팅 메시지 전송
 */
const fetchChatBot = async (message: string, prevChatLogSn?: number) => {
  let msgId: Nullable<number> = null

  if (isLoading.value) return
  try {
    isLoading.value = true
    const payload: ChatBotChattingPostPayload = {
      message,
      uuid: props.uuid,
      ...(prevChatLogSn && { prevChatLogSn }),
    }

    const { content, chatLogSn } = await chatBotStore.chatBotChattingPost(
      payload,
      { signal: chatbotServiceController.value?.signal }
    )
    msgId = chatLogSn

    gtEvent('chatbotEvent', {
      eventCategory: 'chatbot',
      eventAction: 'chatMsg',
      eventLabel: '메시지 보내기',
      eventSlot: '챗봇 모달',
      eventI18nAddr: '',
      eventComponent: 'NovaModalChatbot',
      roomId: props.uuid,
      usage: '사용량 UNDER',
      msgId,
    })

    chatbotContents.value.push({
      role: 'CHATBOT',
      content,
      chatLogSn,
      timestamp: dayjs().format('A hh:mm'),
    })
  } catch (err) {
    // TODO: 사용율 오버하면 상태값 업데이트
    if (axios.isAxiosError(err)) {
      if (err.name === 'CanceledError') return

      switch (err.response?.status) {
        case 9000:
          gtEvent('chatbotEvent', {
            eventCategory: 'chatbot',
            eventAction: 'chatMsg',
            eventLabel: '메시지 보내기',
            eventSlot:
              props.renderType === 'modal' ? '챗봇 모달' : '챗봇 페이지',
            eventI18nAddr: '',
            eventComponent:
              props.renderType === 'modal'
                ? 'NovaModalChatbot'
                : ROUTES.CHATBOT.name,
            roomId: props.uuid,
            usage: '사용량 OVER',
            msgId,
          })
          // 사용량 소진 알림 노출
          isOver.value = true
          break
        case 9003:
          gtEvent('chatbotEvent', {
            eventCategory: 'chatbot',
            eventAction: 'chatMsg',
            eventLabel: '메시지 보내기',
            eventSlot:
              props.renderType === 'modal' ? '챗봇 모달' : '챗봇 페이지',
            eventI18nAddr: '',
            eventComponent:
              props.renderType === 'modal'
                ? 'NovaModalChatbot'
                : ROUTES.CHATBOT.name,
            roomId: props.uuid,
            usage: '챗봇을 24시간 active 경우 에러',
            msgId,
          })
          // 챗봇을 24시간 active 경우 에러 메세지
          // 모달 useToast(t('chatBot.message.errorMessage.9004'))
          // 페이지 useToast(t('chatBot.message.errorMessage.9004'))
          if (props.renderType === 'modal') {
            // 'modal' 타입일 경우 토스트 메시지를 표시
            useToast(t('chatBot.message.errorMessage.9003'))
          } else if (props.renderType === 'page') {
            // ROUTES.CHATBOT.name일 경우 모달 표시
            await modalShow({
              component: NovaModalDialog,
              bind: {
                name: modalsName.MODAL_DIALOG,
                contentsTextAlign: 'center',
                btns: [
                  {
                    label: t('confirm'),
                    theme: 'primary-blue-dark',
                    size: 32,
                    onClick: async () => {
                      await modalHide(modalsName.MODAL_DIALOG)
                      await useNavigations({ type: 'back' })
                    },
                  },
                ],
              },
              slots: {
                title: t('chatBot.modal.title'),
                content: t('chatBot.message.errorMessage.9003'),
              },
            })
          }
          break
        case 9001:
        default:
          // 일반적인 네트워크 에러메세지 노출
          useToast(t('commonError.network.message'))
          break
      }
    } else {
      useToast(t('commonError.network.message'))
    }
  } finally {
    isLoading.value = false
  }
}

const bodyScrollBottom = async () => {
  await nextTick()
  window.scrollTo({
    top: document.body.scrollHeight,
    behavior: 'smooth',
  })

  console.log('body scroll bottom')
}

if (props.renderType === 'page') {
  watch(chatbotContents.value, () => {
    bodyScrollBottom()
  })
}

onBeforeMount(() => {
  chatbotServiceController.value = new AbortController()
})

onMounted(() => {
  fetchChatBot('')
})

onUnmounted(() => {
  chatbotServiceController.value?.abort()
  chatBotStore.chatBotChattingClose({ uuid: props.uuid })
  gtEvent('chatbotEvent', {
    eventCategory: 'chatbot',
    eventAction: 'chatEnd',
    eventLabel: '',
    eventSlot: props.renderType === 'modal' ? '챗봇 모달' : '챗봇 페이지',
    eventI18nAddr: '',
    eventComponent:
      props.renderType === 'modal' ? 'NovaModalChatbot' : ROUTES.CHATBOT.name,
    roomId: props.uuid,
  })
})
</script>

<template>
  <div :class="['chatbot-wrap', `render-${props.renderType}`]">
    <NovaLayoutScrollContainer
      id="modal-chatbot-scroll-container"
      ref="scrollContainerRef"
      class="modal-body"
      :is-rerender-end-scroll-to-bottom="renderType === 'modal'"
    >
      <div class="chat-log">
        <div
          v-for="(item, index) in chatbotContents"
          :key="index"
          :class="['message', item.role.toLowerCase()]"
        >
          <div class="avatar">
            <img
              :src="genProfileImage(item.role)"
              alt="I LIKE LM"
              title="I LIKE LM"
              :class="{
                default: item.role === 'USER' && !user?.userProflUrl,
              }"
            />
          </div>
          <div class="content-box">
            <div
              v-dompurify-html="convertedMarkdownToHtml(item.content)"
              class="content markdown-style"
            />
            <div class="date">{{ item.timestamp }}</div>
          </div>
        </div>
        <div v-if="isLoading" class="message loader" />
      </div>
    </NovaLayoutScrollContainer>

    <!-- 서비스 사용량 -->

    <div
      v-if="isOver"
      v-dompurify-html="t('chatBot.modal.message.over')"
      class="service-usage"
    />

    <!-- 입력 -->
    <div class="chat-input-box-wrap">
      <div class="chat-input-box">
        <textarea
          ref="chatInput"
          :key="chatInputRerenderKey"
          v-model="message"
          class="chat-input"
          rows="1"
          :placeholder="t('chatBot.chatForm.placeholder')"
          :disabled="isOver"
          @input="autoResize"
          @keydown.enter="handleOnKeydown"
          @keyup.enter="handleNewMessage"
        />
        <!--            @keyup.shift.enter="handleAddNewLine"-->
        <button
          :class="['chat-send-button', { disabled: isLoading || isOver }]"
          @click="
            () => {
              handleNewMessage()
            }
          "
        />
      </div>
    </div>
  </div>
</template>

<style scoped lang="scss">
.chatbot-wrap {
  position: relative;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  gap: 12px;
  height: calc(100% - 75.8px);
  padding: 0 24px 130px 24px;
  @include mobile {
    height: calc(100% - 56px);
    padding: 0 12px 94px 12px;
  }
  &.render-page {
    padding: 20px 12px 80px 12px;
    .chat-input-box-wrap {
      position: fixed;
    }
  }
  .service-usage {
    padding: 12px 12px;
    border: 1px solid $color-black-30p;
    border-radius: 10px;
    @include text-style($text-body-14-regular);
    color: $color-text-12;
    word-break: break-all;
    @include mobile {
      padding: 12px 16px;
    }

    :deep(strong) {
      display: inline-block;
      @include text-style($text-body-14-medium);
      color: $color-black;
    }
  }

  .chat-input-box-wrap {
    position: absolute;
    width: 100%;
    bottom: 0;
    left: 0;
    padding-left: 24px;
    padding-right: 24px;
    padding-bottom: calc(env(safe-area-inset-bottom) + 48px);
    background-color: $color-white;
    @include mobile {
      padding-left: 12px;
      padding-right: 12px;
      padding-bottom: calc(env(safe-area-inset-bottom) + 20px);
    }

    .chat-input-box {
      position: relative;
      padding: 20px 60px 14px 16px;
      border: 1px solid $color-black-30p;
      border-radius: 30px;
      background-color: $color-black-5p;
      @include mobile {
        padding: 14px 50px 10px 16px;
      }

      .chat-input {
        width: 100%;
        resize: none;
        padding: 0;
        overflow: hidden;
        max-height: 96px; /* 24px * 3줄 + padding */
        box-sizing: border-box;
        background-color: transparent;
        @include text-style($text-body-14-regular);
        line-height: 20px;

        &::placeholder {
          color: $color-text-3;
        }
      }

      .chat-send-button {
        position: absolute;
        bottom: 10px;
        right: 10px;
        display: flex;
        justify-content: center;
        align-items: center;
        width: 40px;
        height: 40px;
        border-radius: 50%;
        z-index: 10;
        cursor: pointer;
        background: $color-blue-900
          url('@assets/images/icon-chatbot-send.svg?url') no-repeat center
          center;
        transition: all 0.3s ease;
        @include mobile {
          bottom: 5px;
          right: 5px;
        }

        &.disabled {
          opacity: 0.8;
          cursor: default;
          pointer-events: none;
        }
      }
    }
  }
}

/* 챗봇 로그*/
.chat-log {
  display: flex;
  flex-direction: column;
  gap: 40px;
  @include mobile {
    gap: 32px;
  }
}

.message {
  display: flex;
  justify-content: flex-start;
  gap: 16px;
  @include mobile {
    gap: 6px;
  }
  &.user {
    .avatar {
      img {
        width: 100%;
        height: 100%;
        object-fit: cover;
        object-position: center;
        &.default {
          width: 24px;
          height: 24px;
          @include mobile {
            width: 16px;
            height: 16px;
          }
        }
      }
    }
  }
  .avatar {
    display: flex;
    justify-content: center;
    align-items: center;
    width: 64px;
    height: 64px;
    border-radius: 50%;
    background-color: $color-black-5p;
    overflow: hidden;
    @include mobile {
      width: 32px;
      height: 32px;
    }
  }

  .content-box {
    display: flex;
    flex-direction: column;
    gap: 5px;
    max-width: calc(100% - 64px - 64px - 32px);
    @include mobile {
      max-width: calc(100% - 32px - 32px - 12px);
    }

    .content {
      padding: 20px 20px;
      @include text-style($text-display-regular);
      color: $color-black;
      border-radius: 10px;
      background-color: $color-black-5p;
      word-break: break-word;
      @include mobile {
        padding: 12px 14px;
        @include text-style($text-body-14-regular);
      }

      :deep(h1) {
        @include text-style($text-heading1-bold);
      }

      :deep(h2) {
        @include text-style($text-heading2-bold);
      }

      :deep(h3) {
        @include text-style($text-heading3-bold);
      }

      :deep(pre) {
        white-space: pre-wrap; /* 텍스트 줄 바꿈 허용 */
        word-break: break-word; /* 단어 중간에서 줄 바꿈 가능 */
        overflow-wrap: break-word; /* 긴 단어도 깨질 때 줄 바꿈 */
        overflow: auto; /* 넘칠 경우 스크롤 생성 (수직/수평 모두) */
        max-width: 100%; /* 화면 크기에 따라 조정 */
        padding: 6px 10px; /* 내부 여백 */
        background-color: $color-bg-custom-1; /* 약간의 배경색 추가 */
        border-radius: 5px; /* 테두리 둥글기 */
        box-sizing: border-box;
        margin: 4px 0;
      }

      :deep(code) {
        white-space: pre-wrap; /* 줄 바꿈 허용 */
        word-break: break-word; /* 단어가 너무 길 경우 깨짐 */
        color: $color-black; /* 코드 텍스트 색상 */
      }
    }

    .date {
      @include text-style($text-body-14-regular);
      @include mobile {
        @include text-style($text-body-12-regular);
      }
    }
  }

  &.user {
    flex-direction: row-reverse;

    .content-box {
      .date {
        text-align: right;
      }
    }
  }

  &.chatbot {
    .avatar {
      img {
        width: 50px;
        @include mobile {
          width: 24px;
        }
      }
    }

    .content-box {
      .content {
        color: $color-white;
        background-color: $color-blue-900;
      }
    }
  }
}

.loader {
  display: inline-block;
  width: 50%;
  height: 60px;
  border-radius: 10px;
  background: linear-gradient(-90deg, #f0f0f0 50%, #d9d9d9 50%);
  background-size: 200% 100%;
  animation: loading 3s infinite;
  @include mobile {
    height: 46px;
  }
}

@keyframes loading {
  0% {
    background-position: 200% 0;
  }
  100% {
    background-position: -200% 0;
  }
}
</style>
